import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, MapPin, Building2 } from 'lucide-react';
import { createSupplier, Customer } from '../lib/customers';
import AddressAutocomplete from './AddressAutocomplete';

interface FormData {
  type: 'person' | 'business';
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

interface CreateSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (supplier: Customer) => void;
  businessId: string;
  initialData?: Partial<FormData>;
}

const isFormValid = (data: FormData) => {
  return data.first_name.trim() && data.last_name.trim() && data.phone.trim();
};

export default function CreateSupplierModal({ isOpen, onClose, onSuccess, businessId, initialData }: CreateSupplierModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerType, setCustomerType] = useState('person');
  const [formData, setFormData] = useState<FormData>({
    type: 'person',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Set initial data if provided
      if (initialData) {
        setFormData(prev => ({
          ...prev,
          first_name: initialData.first_name || '',
          last_name: initialData.last_name || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          address: initialData.address || '',
          notes: initialData.notes || ''
        }));
      }
    }
  }, [isOpen, initialData]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset form data when modal closes
      setFormData({
        type: 'person',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        notes: ''
      });
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const supplier = await createSupplier({
        business_id: businessId,
        type: formData.type,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        status: 'active',
        amount_spent: 0,
        credit_balance: 0,
        notes: formData.notes || null,
        tax_exempt: false,
        workflow_exempt: false,
        carfax_exempt: false,
        receive_reminders: false,
        receive_offers: false
      });

      onSuccess(supplier);
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div 
        className={`bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'scale-100' : 'scale-95'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Create Supplier</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Supplier Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Supplier Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerType('person');
                      setFormData(prev => ({ ...prev, type: 'person' }));
                    }}
                    className={`flex items-center p-4 border rounded-lg ${
                      customerType === 'person'
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">Person</div>
                      <div className="text-sm text-gray-500">Individual supplier</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerType('business');
                      setFormData(prev => ({ ...prev, type: 'business' }));
                    }}
                    className={`flex items-center p-4 border rounded-lg ${
                      customerType === 'business'
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">Business</div>
                      <div className="text-sm text-gray-500">Company supplier</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {customerType === 'business' ? 'Business Name' : 'First Name'} *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={customerType === 'business' ? 'Enter business name' : 'Enter first name'}
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {customerType === 'business' ? 'Contact Person' : 'Last Name'} *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={customerType === 'business' ? 'Enter contact person name' : 'Enter last name'}
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email address (optional)"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                      onSelect={(address) => setFormData(prev => ({ ...prev, address: address.formatted }))}
                      placeholder="Enter address (optional)"
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                  placeholder="Add any additional notes about this supplier..."
                />
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading || !isFormValid(formData)}
                className={`w-full px-4 py-2 text-sm font-medium text-white ${
                  isFormValid(formData) ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-300 cursor-not-allowed'
                } rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Creating...' : 'Create Supplier'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 